import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import AppBar from '../components/AppBar';
import { Chip, Skeletons, RM, initials } from '../components/kit';
import { renLabel } from '../lib/format';
import { partyWords } from '../lib/partyWords';
import { splitLinkedForms, agreementLabel } from '../lib/saleLinkedForms';
import * as branchSalesApi from '../api/branchSales';
import BranchSplit from '../components/BranchSplit';
import AgentBranch from '../components/AgentBranch';

// Same dot colours as the sale detail's stage card.
const STAGE_COLOR = {
  Draft: '#8c8c8c', Balance: '#1890ff', 'Request invoice': '#1890ff', 'Request receipt': '#faad14',
  'Ready to claim': '#faad14', 'Documents completed': '#13c2c2', 'HQ verified': '#52c41a',
  'HQ approved': '#389e0d', Paid: '#237804', Cancelled: '#ff4d4f', Rejected: '#ff4d4f',
};

/**
 * A branch sale, read-only, for the branch's Centre Director.
 *
 * The CD sees the deal: where it stands, who is on it, the parties, the price, how
 * the commission splits and how much of the fee has come in. What stays with the
 * agents on the deal: every change, the receipts and the comment thread.
 */
export default function BranchSalePage() {
  const { id } = useParams();
  const [sale, setSale] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => {
    branchSalesApi.getBranchSale(id).then((r) => setSale(r?.data ?? r)).catch((e) => setErr(e.message));
  }, [id]);
  const words = partyWords(sale?.type);
  const col = sale?.collection;

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <AppBar back title={sale?.ref || 'Sale'} sub={sale ? `${sale.type} · ${sale.priceF}` : ''}
        right={sale ? <Chip label={sale.status} /> : null} />
      {!sale ? (
        <div style={{ padding: 18 }}>{err ? <div style={{ color: 'var(--muted)', fontSize: 13 }}>{err}</div> : <Skeletons n={3} height={70} />}</div>
      ) : (
        <div className="scrx" style={{ flex: 1, overflowY: 'auto', padding: '16px 16px calc(28px + env(safe-area-inset-bottom))', display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div data-tour="branch-readonly" style={{ display: 'flex', gap: 10, alignItems: 'flex-start', padding: '11px 13px', borderRadius: 14, background: 'var(--chip)', border: '1px solid color-mix(in srgb,var(--primary) 25%,transparent)' }}>
            <span style={{ fontSize: 16, lineHeight: '18px' }}>👁</span>
            <div style={{ fontSize: 12, lineHeight: 1.45, color: 'var(--text)' }}>
              <b>Branch view · read only.</b> You are seeing this sale as Centre Director. Only the agents on the deal and HQ can change it.
            </div>
          </div>

          <div data-tour="branch-stage" style={{ ...rowCard, display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ width: 7, height: 7, borderRadius: '50%', background: STAGE_COLOR[sale.stageLabel] || '#8c8c8c' }} />
                <span style={sectionLabel}>STAGE</span>
              </div>
              <div style={{ fontSize: 15, fontWeight: 700, marginTop: 3 }}>{sale.stageLabel}</div>
              <div style={{ fontSize: 11.5, color: 'var(--muted)' }}>{sale.stage_locked_by_hq ? 'with HQ' : 'with the agent'}</div>
            </div>
          </div>

          <Card>
            <KV k="Reference" v={sale.ref} mono />
            <KV k="Case no." v={sale.case_number || <Muted>Not assigned yet</Muted>} mono={!!sale.case_number} />
            {(() => {
              const { agreements } = splitLinkedForms(sale.booking_forms);
              return <KV k={agreementLabel(sale.type === 'Rent')} v={agreements.length ? agreements.map((f) => f.form_ref).join(', ') : <Muted>None linked</Muted>} mono={agreements.length > 0} />;
            })()}
            <KV k="Property" v={sale.title} />
            <KV k="Type" v={sale.type} />
          </Card>

          <div style={sectionLabel}>AGENTS ON THIS DEAL</div>
          <Card>
            {(sale.parties?.agents || []).map((a, i, arr) => (
              <div key={i} data-tour="branch-agent" style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '11px 0', borderBottom: i < arr.length - 1 ? '1px solid var(--border)' : 'none' }}>
                <div style={{ width: 36, height: 36, borderRadius: '50%', flex: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11.5, fontWeight: 700, background: 'var(--chip)', color: 'var(--primary)' }}>{initials(a.name)}</div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.name}</div>
                  {a.ren && <div style={{ fontSize: 11.5, color: 'var(--muted)', fontFamily: 'ui-monospace,monospace' }}>{renLabel(a.ren)}</div>}
                  <AgentBranch agent={a} />
                </div>
                <span style={{ fontSize: 11.5, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{a.split_percentage != null ? `${a.split_percentage}% split` : '—'}</span>
              </div>
            ))}
          </Card>

          <div style={sectionLabel}>PARTIES</div>
          <Card>
            <KV k={words.vendor} v={sale.parties?.vendor} />
            <KV k={words.purchaser} v={sale.parties?.purchaser} />
            <KV k="Co-agency" v={sale.parties?.coAgency} />
          </Card>

          <div style={sectionLabel}>DEAL</div>
          <Card>
            <KV k="Sale date" v={sale.financials?.sale_date || '—'} />
            <KV k={sale.type === 'Rent' ? 'Monthly rental' : 'Gross sales'} v={RM(sale.financials?.gross_sales_amount)} />
            <KV k="Commission" v={`${sale.financials?.commission_percentage ?? '—'}%  ·  ${RM(sale.financials?.total_commission)}`} />
            <KV k="Net GCI" v={RM(sale.financials?.cid_net_gci ?? sale.financials?.net_gci)} />
          </Card>

          <div style={sectionLabel}>HOW THIS DEAL SPLITS</div>
          <BranchSplit split={sale.commission_split} />

          <div data-tour="branch-collection" style={{ ...rowCard, display: 'flex', flexDirection: 'column', gap: 8 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
              <span style={sectionLabel}>FEE COLLECTED</span>
              <span style={{ fontSize: 13, fontWeight: 700 }}>{col ? `${col.pct}%` : '—'}</span>
            </div>
            <div style={{ height: 8, borderRadius: 99, background: 'var(--surface2)', overflow: 'hidden' }}>
              <div style={{ width: `${col ? col.pct : 0}%`, height: '100%', background: 'var(--ok, #2F9E6F)' }} />
            </div>
            <div style={{ fontSize: 11.5, color: 'var(--muted)' }}>
              {col ? `${RM(col.received)} of ${RM(col.collectible)} received from the client` : 'Not invoiced yet'}
            </div>
          </div>

          <div style={{ fontSize: 11.5, color: 'var(--muted)', lineHeight: 1.5, textAlign: 'center', padding: '4px 10px' }}>
            Receipts and comments stay with the agents on the deal.
          </div>
        </div>
      )}
    </div>
  );
}

const sectionLabel = { fontSize: 11.5, fontWeight: 700, letterSpacing: '.08em', color: 'var(--muted)' };
const rowCard = { background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 14, padding: '15px 16px' };
const Card = ({ children }) => <div className="kv-card" style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 14, padding: '6px 14px' }}>{children}</div>;
const Muted = ({ children }) => <span style={{ color: 'var(--muted)', fontWeight: 500 }}>{children}</span>;
const KV = ({ k, v, mono }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 14, padding: '10px 0', borderBottom: '1px solid var(--border)' }}>
    <span style={{ fontSize: 13, color: 'var(--muted)' }}>{k}</span>
    <span style={{ fontSize: 13, fontWeight: 600, fontFamily: mono ? 'ui-monospace,monospace' : 'inherit', textAlign: 'right' }}>{v}</span>
  </div>
);
